import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Cookie, Settings, Check, X } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/**
 * CookieConsent - PECR Compliant Cookie Banner
 * 
 * UK Privacy and Electronic Communications Regulations (PECR) require:
 * - Clear information about cookies before they are set
 * - User consent before non-essential cookies
 * - Easy way to withdraw consent
 * 
 * Cookie Categories:
 * - Essential: Required for site operation (always enabled)
 * - Functional: Enhanced features, preferences
 * - Analytics: Usage statistics
 * - Marketing: Advertising (currently not used)
 */
const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be changed
    functional: false,
    analytics: false,
    marketing: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Check if user has already set cookie preferences
    const consentId = localStorage.getItem('cookieConsentId');
    const hasConsent = localStorage.getItem('cookieConsent');
    
    if (!hasConsent) {
      // Small delay before showing banner for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else if (hasConsent) {
      // Load saved preferences
      try {
        const saved = JSON.parse(hasConsent);
        setPreferences(prev => ({ ...prev, ...saved }));
      } catch (e) {
        // Invalid stored data, show banner again
        setShowBanner(true);
      }
    }
  }, []);

  const saveConsent = async (prefs) => {
    setSaving(true);
    try {
      // Save to backend for compliance records
      const response = await axios.post(`${API_URL}/api/compliance/cookies/consent`, prefs);
      const consentId = response.data.consent_id;
      
      // Save locally
      localStorage.setItem('cookieConsent', JSON.stringify(prefs));
      localStorage.setItem('cookieConsentId', consentId);
      localStorage.setItem('cookieConsentDate', new Date().toISOString());
      
      setShowBanner(false);
      setShowPreferences(false);
    } catch (error) {
      // Still save locally even if backend fails
      localStorage.setItem('cookieConsent', JSON.stringify(prefs));
      localStorage.setItem('cookieConsentDate', new Date().toISOString());
      setShowBanner(false);
      setShowPreferences(false);
    } finally {
      setSaving(false);
    }
  };

  const acceptAll = () => {
    const allAccepted = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const acceptEssential = () => {
    const essentialOnly = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    setPreferences(essentialOnly);
    saveConsent(essentialOnly);
  };

  const savePreferences = () => {
    saveConsent(preferences);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Main Banner */}
      {!showPreferences && (
        <div 
          className="fixed bottom-0 left-0 right-0 z-[9999] p-4 border-t"
          style={{ 
            backgroundColor: '#F7F4ED',
            borderColor: '#E5E1D8',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.1)'
          }}
          role="dialog"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-description"
          data-testid="cookie-consent-banner"
        >
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Cookie className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: '#A8C3A0' }} />
                <div>
                  <h2 
                    id="cookie-banner-title"
                    className="font-semibold text-sm mb-1"
                    style={{ color: '#2F4632' }}
                  >
                    Cookie Preferences
                  </h2>
                  <p 
                    id="cookie-banner-description"
                    className="text-xs"
                    style={{ color: '#666' }}
                  >
                    We use cookies to provide essential functionality and improve your experience. 
                    Some cookies are necessary for the site to work, while others help us understand 
                    how you use CareSkillset. You can choose which optional cookies to accept.
                  </p>
                  <button 
                    onClick={() => setShowPreferences(true)}
                    className="text-xs underline mt-1 hover:no-underline"
                    style={{ color: '#2F4632' }}
                  >
                    Learn more about our cookies
                  </button>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2 lg:flex-nowrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreferences(true)}
                  className="rounded-lg text-xs"
                  style={{ borderColor: '#2F4632', color: '#2F4632' }}
                  data-testid="cookie-preferences-btn"
                >
                  <Settings className="w-3 h-3 mr-1" />
                  Preferences
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={acceptEssential}
                  disabled={saving}
                  className="rounded-lg text-xs"
                  style={{ borderColor: '#2F4632', color: '#2F4632' }}
                  data-testid="cookie-reject-btn"
                >
                  Essential Only
                </Button>
                <Button
                  size="sm"
                  onClick={acceptAll}
                  disabled={saving}
                  className="rounded-lg text-xs"
                  style={{ backgroundColor: '#A8C3A0', color: '#fff' }}
                  data-testid="cookie-accept-btn"
                >
                  <Check className="w-3 h-3 mr-1" />
                  Accept All
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50">
          <div 
            className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}
            role="dialog"
            aria-labelledby="cookie-preferences-title"
            data-testid="cookie-preferences-modal"
          >
            <div className="p-6 border-b" style={{ borderColor: '#E5E1D8' }}>
              <div className="flex items-center justify-between">
                <h2 
                  id="cookie-preferences-title"
                  className="font-semibold text-lg"
                  style={{ color: '#2F4632' }}
                >
                  Cookie Preferences
                </h2>
                <button 
                  onClick={() => setShowPreferences(false)}
                  className="p-1 hover:bg-slate-100 rounded-lg"
                  aria-label="Close preferences"
                >
                  <X className="w-5 h-5" style={{ color: '#666' }} />
                </button>
              </div>
              <p className="text-sm mt-2" style={{ color: '#666' }}>
                Manage your cookie preferences. Essential cookies cannot be disabled as they are 
                required for the website to function.
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Essential Cookies */}
              <div className="p-4 rounded-xl" style={{ backgroundColor: '#F7F4ED' }}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-sm" style={{ color: '#2F4632' }}>
                    Essential Cookies
                  </h3>
                  <span 
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: '#A8C3A0', color: '#fff' }}
                  >
                    Always On
                  </span>
                </div>
                <p className="text-xs" style={{ color: '#666' }}>
                  These cookies are necessary for the website to function and cannot be switched off. 
                  They include session cookies, authentication tokens, and security features.
                </p>
              </div>

              {/* Functional Cookies */}
              <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E1D8' }}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-sm" style={{ color: '#2F4632' }}>
                    Functional Cookies
                  </h3>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.functional}
                      onChange={(e) => setPreferences(p => ({ ...p, functional: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"></div>
                  </label>
                </div>
                <p className="text-xs" style={{ color: '#666' }}>
                  Enable enhanced functionality such as remembering your theme preference (light/dark mode) 
                  and other personalisation settings.
                </p>
              </div>

              {/* Analytics Cookies */}
              <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E1D8' }}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-sm" style={{ color: '#2F4632' }}>
                    Analytics Cookies
                  </h3>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences(p => ({ ...p, analytics: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"></div>
                  </label>
                </div>
                <p className="text-xs" style={{ color: '#666' }}>
                  Help us understand how visitors interact with CareSkillset by collecting anonymous 
                  usage data. This helps us improve our content and user experience.
                </p>
              </div>

              {/* Marketing Cookies */}
              <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E1D8' }}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-sm" style={{ color: '#2F4632' }}>
                    Marketing Cookies
                  </h3>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences(p => ({ ...p, marketing: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"></div>
                  </label>
                </div>
                <p className="text-xs" style={{ color: '#666' }}>
                  Currently not in use. If enabled in future, these cookies would be used to deliver 
                  relevant advertisements based on your interests.
                </p>
              </div>
            </div>

            <div className="p-6 border-t flex gap-3" style={{ borderColor: '#E5E1D8' }}>
              <Button
                variant="outline"
                onClick={acceptEssential}
                disabled={saving}
                className="flex-1 rounded-xl"
                style={{ borderColor: '#2F4632', color: '#2F4632' }}
              >
                Essential Only
              </Button>
              <Button
                onClick={savePreferences}
                disabled={saving}
                className="flex-1 rounded-xl"
                style={{ backgroundColor: '#A8C3A0', color: '#fff' }}
              >
                Save Preferences
              </Button>
            </div>

            <div className="px-6 pb-6">
              <p className="text-xs text-center" style={{ color: '#999' }}>
                You can change your preferences at any time from the footer of any page.
                <br />
                For more information, see our{' '}
                <a href="/cookie-policy" className="underline hover:no-underline" style={{ color: '#2F4632' }}>
                  Cookie Policy
                </a>{' '}
                and{' '}
                <a href="/privacy-policy" className="underline hover:no-underline" style={{ color: '#2F4632' }}>
                  Privacy Policy
                </a>.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
